/**
 * Contain-authority outcome handling.
 *
 * The server runs the three legs of the owner-offboard cascade independently and answers with what
 * actually committed. A full run is 200; a partial run is 502 with the SAME body, `partial: true`
 * and the failed legs named. The console must surface that partial truth — which legs failed and
 * what was already irreversibly revoked — rather than a bare server error, so the operator retries
 * knowing what remains.
 */
import { ApiError } from "@/lib/api-client";
import { containAuthorityResponseSchema, type ContainAuthorityResponse } from "./schemas";

export type ContainmentLeg = ContainAuthorityResponse["failed_legs"][number];

const LEG_LABEL: Record<ContainmentLeg, string> = {
  standing_delegations: "standing delegations",
  live_grants: "live grants",
  owned_principals: "owned principals",
};

/**
 * Returns the containment result carried by a 502 partial-containment answer, or null when the
 * error is anything else (which the caller rethrows).
 */
export function partialContainmentFrom(err: unknown): ContainAuthorityResponse | null {
  if (!(err instanceof ApiError) || err.status !== 502) return null;
  const parsed = containAuthorityResponseSchema.safeParse(err.body);
  return parsed.success ? parsed.data : null;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export interface ContainmentSummary {
  tone: "success" | "warning";
  message: string;
}

/** Operator-facing summary: the per-leg counts, and for a partial run the legs still to retry. */
export function describeContainment(res: ContainAuthorityResponse): ContainmentSummary {
  const counts =
    `${plural(res.standing_revoked, "standing delegation", "standing delegations")} and ` +
    `${plural(res.grants_revoked, "live grant", "live grants")} revoked, ` +
    `${plural(res.principals_suspended, "owned principal", "owned principals")} suspended`;
  if (!res.partial) {
    return { tone: "success", message: `Non-human authority contained — ${counts}.` };
  }
  const failed = res.failed_legs.map((leg) => LEG_LABEL[leg]).join(", ");
  return {
    tone: "warning",
    message: `Containment incomplete — ${counts}. Not completed: ${failed}. Run it again to finish.`,
  };
}
